/**
 * Service pour la gestion des organisations.
 *
 * Fournit des méthodes pour :
 * - createOrganisation     : créer une nouvelle organisation.
 * - searchOrganisations    : récupérer la liste paginée des organisations.
 * - getOrganisationById    : récupérer une organisation par son ID.
 * - getOrganisationContactAdress : récupérer une organisation avec ses adresses et contacts.
 * - getOrganisationBySlug  : récupérer une organisation par son slug.
 * - updateOrganisation     : mettre à jour une organisation.
 * - deactivateOrganisation : désactiver une organisation.
 * - updateOrganisationLogo    : mettre à jour le logo d'une organisation.
 * - getOrganisationByTaxId    : récupérer une organisation par son numéro de TVA.
 *
 * Utilise axiosInstance pour les requêtes HTTP avec interceptors configurés.
 */
import type { AxiosResponse } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import type {
  ICreateOrganisationBody,
  IUpdateOrganisationBody,
  IApiResponseManageOrganisation,
  IManageOrganisation,
  IApiResponseOrganisationWithDetails,
} from '@/types/manageOrgCommerce.types';

export const ManageOrganisationService = {
  /**
   * Crée une nouvelle organisation.
   * @param data - Corps de la requête (ICreateOrganisationBody)
   * @returns Promise contenant la réponse Axios
   */
  createOrganisation: async (
    data: ICreateOrganisationBody,
  ): Promise<AxiosResponse<{ data: IManageOrganisation; message: string }>> => {
    try {
      const response = await axiosInstance.post<{
        data: IManageOrganisation;
        message: string;
      }>('organizations/create', data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Récupère la liste paginée des organisations.
   * @param page - Numéro de page (défaut : 1)
   * @param status - Filtres de statut
   * @param sortBy - Champ de tri
   * @param sortOrder - Ordre de tri (ASC | DESC)
   * @returns Promise contenant la réponse Axios
   */
  searchOrganisations: async (
    page = 1,
    status: string[] = [],
    sortBy = '',
    sortOrder = '',
  ): Promise<AxiosResponse<IApiResponseManageOrganisation>> => {
    try {
      const params = new URLSearchParams();
      params.set('pagination', 'true');
      params.set('page', String(page));
      status.forEach((s) => params.append('status', s));
      if (sortBy) params.set('sortBy', sortBy);
      if (sortOrder) params.set('sortOrder', sortOrder);
      const response = await axiosInstance.get<IApiResponseManageOrganisation>(
        `organizations/search?${params.toString()}`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Récupère une organisation par son ID.
   * @param organizationId - UUID de l'organisation
   * @returns Promise contenant la réponse Axios
   */
  getOrganisationById: async (
    organizationId: string,
  ): Promise<AxiosResponse<IManageOrganisation>> => {
    try {
      const response = await axiosInstance.get<IManageOrganisation>(
        `organizations/get/${organizationId}`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Récupère une organisation avec ses adresses et contacts.
   * @param organizationId - UUID de l'organisation
   * @returns Promise contenant la réponse Axios
   */
  getOrganisationContactAdress: async (
    organizationId: string,
  ): Promise<AxiosResponse<IApiResponseOrganisationWithDetails>> => {
    try {
      const response =
        await axiosInstance.get<IApiResponseOrganisationWithDetails>(
          `organizations/get/${organizationId}/full`,
        );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Récupère une organisation par son slug.
   * @param slug - Slug de l'organisation
   * @returns Promise contenant la réponse Axios
   */
  getOrganisationBySlug: async (
    slug: string,
  ): Promise<AxiosResponse<IManageOrganisation>> => {
    try {
      const response = await axiosInstance.get<IManageOrganisation>(
        `organizations/slug/${slug}`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Met à jour une organisation existante.
   * @param organizationId - UUID de l'organisation
   * @param data - Corps de la requête (IUpdateOrganisationBody)
   * @returns Promise contenant la réponse Axios
   */
  updateOrganisation: async (
    organizationId: string,
    data: IUpdateOrganisationBody,
  ): Promise<AxiosResponse<{ data: IManageOrganisation; message: string }>> => {
    try {
      const response = await axiosInstance.put<{
        data: IManageOrganisation;
        message: string;
      }>(`organizations/update/${organizationId}`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Désactive une organisation.
   * @param organizationId - UUID de l'organisation
   * @returns Promise contenant la réponse Axios
   */
  deactivateOrganisation: async (
    organizationId: string,
  ): Promise<AxiosResponse<{ message: string }>> => {
    try {
      const response = await axiosInstance.put<{ message: string }>(
        `organizations/update/${organizationId}/deactivate`,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Met à jour le logo d'une organisation (multipart/form-data).
   * @param organizationId - UUID de l'organisation
   * @param file - Fichier image du logo
   * @returns Promise contenant la réponse Axios
   */
  updateOrganisationLogo: async (
    organizationId: string,
    file: File,
  ): Promise<AxiosResponse<{ data: IManageOrganisation; message: string }>> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.put<{
        data: IManageOrganisation;
        message: string;
      }>(`organizations/${organizationId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Associe un logo (déjà uploadé) à une organisation via son ID.
   * @param organizationId - UUID de l'organisation
   * @param logoId - Identifiant du fichier logo, ou null pour le supprimer
   * @returns Promise contenant la réponse Axios
   */
  setOrganisationLogoId: async (
    organizationId: string,
    logoId: string | null,
  ): Promise<AxiosResponse<{ data: IManageOrganisation; message: string }>> => {
    try {
      const response = await axiosInstance.put<{
        data: IManageOrganisation;
        message: string;
      }>(
        `organizations/${organizationId}/logo`,
        { logoId },
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Récupère une organisation par son numéro d'identification fiscale (TVA).
   * @param taxIdentifier - Numéro TVA de l'organisation
   * @returns Promise contenant la réponse Axios
   */
  getOrganisationByTaxId: async (
    taxIdentifier: string,
  ): Promise<AxiosResponse<IManageOrganisation>> => {
    try {
      const response = await axiosInstance.get<IManageOrganisation>(
        `organizations/taxIdentifier/${taxIdentifier}`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },
};
